import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CityDetail from '../components/CityDetail';
import { LegacyCity, LegacyJob } from '../types';
import { trackCityViewed } from '../src/lib/analytics';
import { useCities } from '../src/contexts/CitiesContext';
import type { CityFromAPI } from '../src/api/cities';

// CityFromAPI → LegacyCity 适配
const toLegacyCity = (c: CityFromAPI): LegacyCity => ({
  id: c.id,
  name: c.nameZh || c.name,
  enName: c.nameEn || c.enName,
  country: c.country?.nameEn || c.countryCode,
  image: c.image,
  jobCount: c.jobCount,
  isRemoteOnly: c.isRemoteOnly,
});

// 使用 LegacyJob 类型用于向后兼容
type Job = LegacyJob;

const CityDetailPage: React.FC = () => {
  const { cityId } = useParams<{ cityId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { citiesMap } = useCities();

  // 查找城市
  const city = cityId ? citiesMap.get(cityId) : undefined;

  // 更新页面标题
  useEffect(() => {
    if (city) {
      const cityName = i18n.language.startsWith('zh') ? (city.nameZh || city.name) : (city.nameEn || city.enName);
      document.title = `${cityName} - ${t('html.title')}`;
      trackCityViewed(city.id, cityName);
    }
  }, [city, t, i18n.language]);

  // 城市不存在，显示友好的 404 状态
  if (!city) {
    return (
      <div className="min-h-screen bg-slate-50 pt-16">
        <div className="max-w-4xl mx-auto px-4 py-16 sm:py-24 text-center">
          
          {/* 大号 404 数字 */}
          <div className="mb-8">
            <svg 
              viewBox="0 0 400 160" 
              className="w-full max-w-md mx-auto h-auto"
              aria-hidden="true"
            >
              <text 
                x="200" 
                y="110" 
                textAnchor="middle" 
                className="fill-slate-300" 
                style={{ fontSize: '140px', fontWeight: 900, fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                4
                <tspan className="fill-blue-500">0</tspan>
                4
              </text>
            </svg>
          </div>

          {/* 标题 */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-4">
            {t('notFound.cityNotFound', '城市不存在')}
          </h1>

          {/* 描述 */}
          <p className="text-slate-500 text-base sm:text-lg mb-12 max-w-md mx-auto leading-relaxed">
            {t('notFound.cityDescription', '该城市页面不存在或链接有误，请浏览我们的全球办公室')}
          </p>

          {/* 按钮组 */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/offices')}
              className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-xl font-bold text-base hover:bg-blue-600 transition-all shadow-lg shadow-slate-300 active:scale-95 cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 002 2 2 2 0 012 2v.654M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('notFound.viewAllOffices', '查看所有办公室')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-xl font-bold text-base border-2 border-slate-200 hover:border-blue-500 hover:text-blue-600 transition-all active:scale-95 cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              {t('notFound.backHome', '返回首页')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/offices');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleSelectJob = (job: Job) => {
    navigate(`/jobs/${job.id}`, { state: { from: 'city' } });
  };

  return (
    <CityDetail
      city={toLegacyCity(city)}
      onBack={handleBack}
      onBackToHome={handleBackToHome}
      onSelectJob={handleSelectJob}
    />
  );
};

export default CityDetailPage;
